import { useState, useEffect } from 'react';
import { getFeatureFlagVariant, trackEvent } from '../analytics/posthog';

/**
 * Hybrid feature flags: PostHog is the primary source, with a local fallback
 * (URL override → localStorage → random assignment) for resilience and dev.
 */

export type FlagKey = 'kb' | 'duets_enabled' | 'new_landing';

export type FlagValue<K extends FlagKey> = K extends 'kb'
  ? 'classic' | 'new'
  : K extends 'duets_enabled'
  ? boolean
  : K extends 'new_landing'
  ? boolean
  : string;

const STORAGE_KEY = 'karaokr_feature_flags';

interface StoredFlags {
  userId: string;
  assignments: Record<string, string>;
}

function getUserId(): string {
  if (typeof window === 'undefined') return 'server';
  return (
    localStorage.getItem('karaokr_event_user_id') ||
    localStorage.getItem('karaokr_user_id') ||
    `anon-${Math.random().toString(36).slice(2, 10)}`
  );
}

function readStorage(): StoredFlags {
  if (typeof window === 'undefined') return { userId: getUserId(), assignments: {} };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { userId: getUserId(), assignments: {} };
    const parsed = JSON.parse(raw) as StoredFlags;
    return { userId: parsed.userId || getUserId(), assignments: parsed.assignments || {} };
  } catch {
    return { userId: getUserId(), assignments: {} };
  }
}

function writeStorage(data: StoredFlags) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function parseUrlOverrides(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const ff = params.get('ff');
  if (!ff) return {};
  const overrides: Record<string, string> = {};
  ff.split(',').forEach((pair) => {
    const [key, value] = pair.split(':');
    if (key && value) overrides[key.trim()] = value.trim();
  });
  return overrides;
}

function shouldResetFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('ff-reset');
}

function isBooleanFlag(flag: FlagKey): boolean {
  return flag === 'duets_enabled' || flag === 'new_landing';
}

function normalizeVariant<K extends FlagKey>(flag: K, raw: string): FlagValue<K> | null {
  if (flag === 'kb') {
    const value = raw.toLowerCase();
    if (value === 'classic' || value === 'new') return value as FlagValue<K>;
    return null;
  }
  if (isBooleanFlag(flag)) {
    return (raw.toLowerCase() === 'true') as FlagValue<K>;
  }
  return raw as FlagValue<K>;
}

function pickDefaultVariant<K extends FlagKey>(flag: K): FlagValue<K> {
  if (flag === 'kb') {
    return (Math.random() < 0.5 ? 'classic' : 'new') as FlagValue<K>;
  }
  if (isBooleanFlag(flag)) {
    return false as FlagValue<K>;
  }
  return 'control' as FlagValue<K>;
}

export function getFeatureFlag<K extends FlagKey>(flag: K): FlagValue<K> {
  if (shouldResetFromUrl()) {
    writeStorage({ userId: getUserId(), assignments: {} });
  }

  // 1. URL override always wins (useful for QA/dev).
  const overrides = parseUrlOverrides();
  const overrideRaw = overrides[flag];
  if (overrideRaw) {
    const overrideValue = normalizeVariant(flag, overrideRaw);
    if (overrideValue !== null) {
      trackFlagAssignment(flag, overrideRaw, 'url_override');
      return overrideValue;
    }
  }

  // 2. Ask PostHog if available.
  const posthogVariant = getFeatureFlagVariant(flag);
  if (posthogVariant) {
    const variantString = String(posthogVariant);
    const posthogValue = normalizeVariant(flag, variantString);
    if (posthogValue !== null) {
      persistFlag(flag, variantString);
      trackFlagAssignment(flag, variantString, 'posthog');
      return posthogValue;
    }
  }

  // 3. Local fallback: localStorage or random assignment.
  const stored = readStorage();
  const storedValue = stored.assignments[flag];
  if (storedValue) {
    const localValue = normalizeVariant(flag, storedValue);
    if (localValue !== null) {
      trackFlagAssignment(flag, storedValue, 'local_storage');
      return localValue;
    }
  }

  const picked = pickDefaultVariant(flag);
  const pickedString = String(picked);
  stored.assignments[flag] = pickedString;
  writeStorage(stored);

  trackFlagAssignment(flag, pickedString, 'local_random');
  return picked;
}

function persistFlag(flag: FlagKey, value: string) {
  const stored = readStorage();
  if (stored.assignments[flag] === value) return;
  stored.assignments[flag] = value;
  writeStorage(stored);
}

export function setFeatureFlag<K extends FlagKey>(flag: K, value: FlagValue<K>) {
  const stored = readStorage();
  stored.assignments[flag] = String(value);
  writeStorage(stored);
}

export function resetFeatureFlags() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function trackFlagAssignment(flag: string, variant: string, source: string) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[FeatureFlag]', { flag, variant, source });
  }
  trackEvent('feature_flag_assigned', { flag, variant, source });
}

export function trackFlagInteraction(flag: string, variant: string, action: string, metadata?: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('[FeatureFlag] interaction', { flag, variant, action, metadata });
  }
  trackEvent('feature_flag_interaction', { flag, variant, action, ...(metadata ?? {}) });
}

export function useFeatureFlag<K extends FlagKey>(flag: K): FlagValue<K> {
  const [value, setValue] = useState<FlagValue<K>>(() => getFeatureFlag(flag));

  useEffect(() => {
    const handler = () => setValue(getFeatureFlag(flag));
    window.addEventListener('karaokr-flags-changed', handler);
    return () => window.removeEventListener('karaokr-flags-changed', handler);
  }, [flag]);

  return value;
}

// Polyfill a tiny event for cross-tab sync if needed.
declare global {
  interface WindowEventMap {
    'karaokr-flags-changed': CustomEvent<{ flag: string; value: string }>;
  }
}
